import { currencyEquals, MON, NativeCurrency, Token, ChainId } from '@monadex/sdk'
import { useMemo } from 'react'
import { Box } from '@mui/material'
import useHttpLocations from '@/hooks/useHttpLocations'
import { WrappedTokenInfo } from '@/state/list/hooks'
import { Logo } from '@/components'
import { getTokenLogoURL } from '@/utils/getTokenLogoURL'
import { useInActiveTokens } from '@/hooks/Tokens'
import Image from 'next/image'
import { wrappedCurrency } from '@/utils/wrappedCurrency'

interface CurrencyLogoProps {
  currency?: Token | NativeCurrency
  size?: string
  style?: React.CSSProperties
  withoutBg?: boolean
  className?: string
}

const CurrencyLogo: React.FC<CurrencyLogoProps> = ({
  currency,
  size = '24px',
  style,
  withoutBg,
  className
}) => {
  const nativeCurrency = MON
  const nativeCurrencyImage = currency?.symbol ? `/${currency.symbol}.png` : '/default.png' // Fallback image
  const uriLocations = useHttpLocations(
    currency instanceof WrappedTokenInfo
      ? currency.logoURI ?? currency.tokenInfo?.logoURI
      : undefined
  )
  const inactiveTokenList = useInActiveTokens()

  const srcs: string[] = useMemo(() => {
    if (currency == null) return []

    if (currencyEquals(currency, nativeCurrency)) {
      return [nativeCurrencyImage]
    }

    if (currency instanceof WrappedTokenInfo) {
      const tokenLogoUrls = getTokenLogoURL(currency.address ?? currency.tokenInfo.address, inactiveTokenList) || []

      return [...tokenLogoUrls, ...uriLocations]
    }

    if (currency instanceof Token) {

      const tokenLogoUrls = getTokenLogoURL(currency.address, inactiveTokenList) || []
      return tokenLogoUrls
    }

    return []
  }, [currency, inactiveTokenList, nativeCurrency, uriLocations, nativeCurrencyImage])

  if ((currency != null) && currencyEquals(currency, nativeCurrency)) {
    return (
      <Box
        style={style}
        width={size}
        height={size}
        borderRadius={size}
        className={`${className ?? ''} flex content-center items-center overflow-hidden`}
      >
        <Image
          className='w-100 h-100'
          src={nativeCurrencyImage}
          alt='Currency Logo'
          width={parseInt(size)} // Convert size to number
          height={parseInt(size)}
        />
      </Box>
    )
  }

  return (
    <Box
      width={size}
      height={size}
      borderRadius={withoutBg ? '0' : size}
      className={`${className ?? ''} flex content-center items-center overflow-hidden ${
        withoutBg ? '' : 'bg-transparent'
      }`}
    >
      <Logo
        srcs={srcs}
        size={size}
        alt={`${currency?.symbol ?? 'token'} logo`}
        symbol={currency?.symbol}
      />
    </Box>
  )
}

export default CurrencyLogo
